import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Shield, CheckCircle, AlertCircle, LogIn, Loader2 } from "lucide-react";

const AdminSetup = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [setupStatus, setSetupStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/setup/status`);
      setSetupStatus(response.data);
    } catch (error) {
      console.error("Error checking setup status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/admin/setup`, {}, { withCredentials: true });
      toast.success(response.data.message);
      // Refresh auth state
      window.location.href = "/admin";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Setup failed");
    } finally {
      setProcessing(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Checking setup status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-primary-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">Admin Setup</h1>
            <p className="text-gray-600">The Companions Foundation</p>
          </div>

          {setupStatus && !setupStatus.setup_needed ? (
            // Admin already exists
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="font-semibold text-gray-900 mb-2">Setup Complete</h2>
              <p className="text-gray-600 mb-6">
                Admin setup has already been completed. There {setupStatus.admin_count === 1 ? "is" : "are"} {setupStatus.admin_count} admin{setupStatus.admin_count !== 1 ? "s" : ""} in the system.
              </p>
              <Button onClick={() => navigate("/login")} className="w-full btn-primary">
                Go to Login
              </Button>
            </div>
          ) : !user ? (
            // Not logged in
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <h2 className="font-semibold text-gray-900 mb-2">Login Required</h2>
              <p className="text-gray-600 mb-6">
                Please login first to become the first admin. After logging in, return to this page to complete setup.
              </p>
              <Button onClick={() => navigate("/login")} className="w-full btn-primary">
                <LogIn className="w-4 h-4 mr-2" />
                Login First
              </Button>
            </div>
          ) : (
            // Logged in and setup needed
            <div className="text-center">
              <div className="bg-primary-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-primary-800">
                  Logged in as: <strong>{user.email}</strong>
                </p>
              </div>
              <h2 className="font-semibold text-gray-900 mb-2">Become the First Admin</h2>
              <p className="text-gray-600 mb-6">
                No admins exist yet. Click below to make yourself the first admin of The Companions Foundation platform.
              </p>
              <Button 
                onClick={handleSetup} 
                className="w-full btn-primary"
                disabled={processing}
                data-testid="setup-admin-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Make Me Admin
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSetup;
